<template>
  <main-layout>
    <template #header>
      <main-header :title="'프린터 사용량' | translate" />
    </template>
    <template #feature>
      <div style="width: 100%">
        <div class="d-flex align-center justify-space-between">
          <Strong>● 정보</Strong>
          <div class="ml-10 red--text">
            ※ 노락색으로 표기된 항목은 이전 데이터와 현재 데이터가 다른 항목 입니다.
          </div>
        </div>
        <div class="d-flex align-center justify-space-between">
          <div>
            {{ selectPrinter.rentalcompanyName }}({{ selectPrinter.rentalcompanyId }})
            | {{ selectPrinter.accountName || '-' }}({{ selectPrinter.accountId }})
            | {{ selectPrinter.printerModelName }}
            | {{ selectPrinter.printerSerialNo }}
          </div>
        </div>
        <div v-if="!selectPrinter.isP" class="d-flex align-center justify-space-between">
          <div>
            <strong>
              엔진카운트 사용여부: 
            </strong>
            <span>{{ selectPrinter.engineCountYn }}</span>
          </div>
          <div>
            <strong>
              합산정산 사용여부:
            </strong>
            <span>{{ selectPrinter.bundleYn }}</span>
          </div>
          <div>
            <strong>
              이월정산 사용여부:
            </strong>
            <span>{{ selectPrinter.carryUseYn }}</span>
          </div>
          <div>
            <strong>
              모노/컬러 별도 청구 여부: 
            </strong>
            <span>{{ selectPrinter.colorYn }}</span>
          </div>
          <div>
            <strong>
              A3 2배 적용 여부: 
            </strong>
            <span>{{ selectPrinter.a3DoubleCountYn }}</span>
          </div>
          <div>
            <strong>
              모노 기본매수: 
            </strong>
            <span>{{ (selectPrinter.basePagecount || 0).toLocaleString() }} 매</span>
          </div>
          <div>
            <strong>
              컬러 기본매수: 
            </strong>
            <span>{{ (selectPrinter.basePagecountColor || 0).toLocaleString() }} 매</span>
          </div>
          <div>
            <strong>
              기본료: 
            </strong>
            <span class="table-text-left">
              {{ (selectPrinter.baseFee || 0).toLocaleString() }} 원
            </span>
          </div>
          <div>
            <strong>
              모노 추가료: 
            </strong>
            <span>{{ (selectPrinter.additionalFee || 0).toLocaleString() }} 원</span>
          </div>
          <div>
            <strong>
              컬러 추가료: 
            </strong>
            <span>{{ (selectPrinter.additionalFeeColor || 0).toLocaleString() }} 원</span>
          </div>
          <div>
            <strong>
              생성매출(기본/추가):
            </strong>
            <span>
               {{ selectPrinter.billingCnt || 0 }} / {{ selectPrinter.addBillingCnt || 0 }}
            </span>
          </div>
        </div>
      </div>
    </template>
    <template #toolbar>
      <div style="width: 100%">
        <div class="d-flex align-center justify-space-between mt-2 mb-2">
          <div class="d-flex">
            <admin-date-picker
              :label="'시작일'"
              :date.sync="searchOptions.usageStartDate"
            />
            <div class="mr-2" />
            <admin-date-picker
              class="ml-2"
              :label="'종료일'"
              :date.sync="searchOptions.usageEndDate"
            />
          </div>
          <div>
            <v-btn
              v-if="!selectPrinter.isP"
              color="primary"
              dark
              small
              @click.stop="onclickCorrectionMonthly"
            >
              월별사용량 보정
            </v-btn>
          </div>
        </div>
      </div>
    </template>
    <template #body>
      <div class="main-con">
        <table class="data-table" style="table-layout: fixed">
          <thead>
          <tr>
            <th rowspan="2" class="border-right">
              일자
            </th>
            <th colspan="5" class="border-right">
              전일
            </th>
            <th colspan="5" class="border-right">
              당일
            </th>
            <th colspan="5" class="border-right">
              사용량
            </th>
            <th rowspan="2">
              차이
            </th>
            <th rowspan="2">
              보정
            </th>
          </tr>
          <tr>
            <td>M4</td>
            <td>M3</td>
            <td>C4</td>
            <td>C3</td>
            <td class="border-right">
              엔진
            </td>
            <td>M4</td>
            <td>M3</td>
            <td>C4</td>
            <td>C3</td>
            <td class="border-right">
              엔진
            </td>
            <td>M4</td>
            <td>M3</td>
            <td>C4</td>
            <td>C3</td>
            <td class="border-right">
              엔진
            </td>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, idx) in convDataList" :key="`printer-usage-${ item.baseDay }-${ item.printerDayUsageId }`"
              :class="{
            'abnormal': !!item.checkAbnormal
          }"
          >
            <td class="border-right">
              {{ item.baseDay }}
            </td>
            <td class="table-text-right">
              {{ item.pm4 }}
            </td>
            <td class="table-text-right">
              {{ item.pm3 }}
            </td>
            <td class="table-text-right">
              {{ item.pc4 }}
            </td>
            <td class="table-text-right">
              {{ item.pc3 }}
            </td>
            <td class="table-text-right border-right">
              {{ item.pe }}
            </td>
            <td class="table-text-right">
              {{ item.fm4 }}
            </td>
            <td class="table-text-right">
              {{ item.fm3 }}
            </td>
            <td class="table-text-right">
              {{ item.fc4 }}
            </td>
            <td class="table-text-right">
              {{ item.fc3 }}
            </td>
            <td class="table-text-right border-right">
              {{ item.fe }}
            </td>
            <td class="table-text-right">
              {{ (item.fm4 || 0) - (item.pm4 || 0) }}
            </td>
            <td class="table-text-right">
              {{ (item.fm3 || 0) - (item.pm3 || 0) }}
            </td>
            <td class="table-text-right">
              {{ (item.fc4 || 0) - (item.pc4 || 0) }}
            </td>
            <td class="table-text-right">
              {{ (item.fc3 || 0) - (item.pc3 || 0) }}
            </td>
            <td class="table-text-right border-right">
              {{ (item.fe || 0) - (item.pe || 0) }}
            </td>
            <td>
              {{ item.checkAbnormal }}
            </td>
            <td>
              <v-btn
                v-if="!!item.correctionYn && !!item.checkAbnormal"
                color="primary"
                dark
                x-small
                @click.stop="onclickCorrection(item, idx)"
              >
                보정
              </v-btn>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <div v-show="isLoading" class="loading_wrap">
        <div class="loading" />
        <div class="loading" />
        <div class="loading" />
      </div>
    </template>
  </main-layout>
</template>

<script>
/**
 * 매출을 확인할 수 있는 컴포넌트입니다.
 *  1. 메뉴에서 매출을 클릭했을때
 *  2. 배치에서 매출 확인 버튼을 클릭했을때
 */
import MainLayout from "@/components/common/layout/MainLayout";
import SearchBar from "@/components/input/SearchBar";
import MainHeader from "@/components/common/layout/MainHeader";
import SearchGroupSales from "@/views/sales/SearchGroupSales";
import NoLibraryBagicTable from "@/views/sales/NoLibraryBagicTable";
import {PATH} from "@/constants";
import cloneDeep from "@/assets/plugins/mps-common/expansions/clone/cloneDeep";
import AdminDatePicker from "@/components/input/AdminDatePicker.vue";
import {DateTime} from "luxon";

export default {
  name: "UsageList",
  components: {
    AdminDatePicker,
    MainLayout,
    MainHeader,
  },
  props: {
    /**
     * 배치에서 매출부분에 매출 확인 버튼 누르면
     * rentalcompanyId 가 props 로 전달되고 바로 목록을 보여주기 위함입니다.
     */
    selectPrinter: {
      type: Object,
      default: undefined,
    }
  },
  data() {
    return {
      isLoading: false,
      searchOptions: {
        usageStartDate: DateTime.local().minus({months: 1}).toISODate(),
        usageEndDate: DateTime.local().toISODate(),
        monitoringPrinterContractId: undefined,
      },
      dataList: [],
      pageNumber: 1, //추가로드를 위한 데이터입니다.
      disableButton: false//추가로드버튼 클릭은 막기 위함입니다.
    }
  },
  computed: {
    convDataList() {
      const result = []
      this.dataList.forEach((e, idx) => {
        let correctionYn = true
        let checkAbnormal = ''
        if (e.manualTotalCnt > 0 ||
          e.manualMonoCnt > 0 ||
          e.manualColorCnt > 0 ||
          e.register !== 'SYSTEM' ||
          (e.updater !== 'SYSTEM' && !!e.updater)) {
          correctionYn = false
          checkAbnormal += '고객수정'
        }

        if (!e || this.dataList.length === idx + 1) {
          result.push({
            ...e,
            checkAbnormal: undefined,
            correctionYn
          })
        } else {
          const prevDayItem = this.dataList[idx + 1]
          if (
            prevDayItem.fm4 !== e.pm4
          ) {
            checkAbnormal += 'mono4 '
          }
          if (
            prevDayItem.fm3 !== e.pm3
          ) {
            checkAbnormal += 'mono3 '
          }
          if (
            prevDayItem.fc4 !== e.pc4
          ) {
            checkAbnormal += 'color4 '
          }
          if (
            prevDayItem.fc3 !== e.pc3
          ) {
            checkAbnormal += 'color3 '
          }
          if (
            prevDayItem.fe !== e.pe
          ) {
            checkAbnormal += 'engine'
          }
          result.push({
            ...e,
            checkAbnormal: checkAbnormal === '' ? undefined : checkAbnormal,
            correctionYn
          })
        }
      })
      return result
    },
  },

  watch: {
    selectPrinter: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.searchOptions.monitoringPrinterContractId = newVal.monitoringPrinterContractId
        if (newVal.isP) {
          this.searchOptions.usageStartDate = newVal.bDay
          this.searchOptions.usageEndDate = newVal.cDay
        } else {
          this.searchOptions.usageStartDate = newVal.usageStartDate
          this.searchOptions.usageEndDate = newVal.usageEndDate
        }
      },
    },

    searchOptions: {
      immediate: true,
      deep: true,
      handler() {
        this.dataLoad()
      }
    },

  },
  created() {

  },
  methods: {
    async dataLoad() {
      this.isLoading = true;
      this.dataList.splice(0, this.dataList.length)
      await this.$request(PATH.BATCH.PRINTER_USAGE)
        .setObject({
          ...this.searchOptions
        })
        .catch()
        .enqueue()
        .then(res => {
          this.dataList.push(...res.data)
          this.isLoading = false;
        }).catch((e) => {
          this.isLoading = false;
          alert('조회실패')
        });
    },

    async onclickCorrection(item, index) {
      if (!confirm(`${item.baseDay} 해당일의 데이터를 보정하시겠습니까?`)){
        return
      }
      if (this.dataList.length < index + 2) {
        alert('이전 데이터를 찾을 수 업습니다.')
        return
      }
      const cloneData = cloneDeep(item)
      const beforeData = this.dataList[index + 1]
      cloneData.correctionHist = JSON.stringify({changeDate: this.$now().toISO(), ...cloneData})
      cloneData.pm4 = beforeData.fm4
      cloneData.pm3 = beforeData.fm3
      cloneData.pc4 = beforeData.fc4
      cloneData.pc3 = beforeData.fc3
      cloneData.pe = beforeData.fe

      await this.$request(PATH.BATCH.PRINTER_USAGE_CORRECTION)
        .setObject(cloneData)
        .catch()
        .enqueue()
        .then(res => {
          this.isLoading = false;
          this.dataLoad()
        }).catch((e) => {
          this.isLoading = false;
          alert('수정실패')
        });
    },

    async onclickCorrectionMonthly() {
      /*if (this.selectPrinter.billingCnt > 0 && this.selectPrinter.addBillingCnt > 0) {
        alert('이미 정산이 끝난 월별사용량은 수정할 수 없습니다.')
        return
      }*/
      if (this.selectPrinter.bundleYn === 'Y' && this.selectPrinter.carryUseYn === 'Y') {
        if(!confirm(`묶음정산 / 이월정산 대상 입니다. 월별사용량 데이터를 보정하시겠습니까?`)) {
          return
        }
      } else if (!confirm(`월별사용량 데이터를 보정하시겠습니까?`)){
        return
      }

      await this.$request(PATH.BATCH.MONTHLY_USAGE_CORRECTION)
        .setObject(this.selectPrinter)
        .catch()
        .enqueue()
        .then(res => {
          this.isLoading = false;
          this.dataLoad()
        }).catch((e) => {
          this.isLoading = false;
          alert('수정실패')
        });
    },
  }
}
</script>

<style scoped>
/*search 부분이랑 select 부분 wrapper입니다*/
.loading_wrap {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  background: rgba(0, 0, 0, 0.5);
}

.loading {
  width: 8px;
  height: 8px;
  background: #aaaaaa;
  border-radius: 50%;
  animation: loading-cricle 1.5s infinite ease-in-out;
  display: inline-block;
  margin-right: 8px;
}

.loading:nth-child(2) {
  animation-delay: .5s;
}

.loading:nth-child(3) {
  animation-delay: 1s;
}

@keyframes loading-cricle {
  0% {
    opacity: .4;
    transform: scale(1, 1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2, 1.2);
  }
  100% {
    opacity: .4;
    transform: scale(1, 1);
  }
}

/*Search Box 부분 font 랑 사이즈 조정 부분입니다*/
>>> .v-input__control .v-input__slot .v-text-field__slot input {
  font-size: 11px;
  padding: 0;
}

>>> .v-input__control .v-input__slot fieldset {
  height: 47px;
}

.main-con {
  position: relative;
  height: 100%;
  overflow-y: auto;
}

.main-con .data-table {
  position: relative;
  width: 100%;
  border-spacing: 0;
}

.main-con .data-table thead {
  z-index: 1;
  position: sticky;
  top: 0;
  background: #ddd;
}

.main-con .data-table thead tr th {
  font-size: 13px;
}

.main-con .data-table tbody td {
  padding: 0.15rem;
  text-align: left;
  border: 1px solid #ccc;
  font-size: 13px;
}

.main-con .data-table tbody tr.abnormal {
  background: #ffff00;
}

.main-con .data-table >>> th.border-right,
.main-con .data-table >>> td.border-right {
  border-right: 2px solid #333;
}
</style>